
import React, { useState } from 'react';
import { useAppContext } from '../App';
import { UserRole } from '../types';
import { supabase } from '../services/supabaseClient';
import { Settings, Users, UserPlus, Save, AlertTriangle, Check, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const ChurchDashboard: React.FC = () => {
    const { currentUser, churches, updateChurch } = useAppContext();
    const myChurch = churches.find(c => c.id === currentUser?.churchId);
    
    const [activeTab, setActiveTab] = useState<'info' | 'team'>('info');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

    // Church Info State
    const [churchData, setChurchData] = useState({
        name: myChurch?.name || '',
        description: myChurch?.description || '',
        website: myChurch?.website || '',
        address: myChurch?.address || ''
    });

    // New Pastor State
    const [newPastor, setNewPastor] = useState({
        email: '',
        name: '',
        password: ''
    });

    if (!currentUser || currentUser.role !== UserRole.CHURCH_REP || !myChurch) {
        return <div className="p-10 text-center">Access Denied. Church Representatives Only.</div>;
    }

    const handleUpdateInfo = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage(null);
        try {
            await updateChurch({
                ...myChurch,
                ...churchData
            });
            setMessage({ type: 'success', text: "Church information updated successfully." });
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreatePastor = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage(null);
        try {
            // Create Auth User
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: newPastor.email,
                password: newPastor.password,
                options: {
                    data: { name: newPastor.name }
                }
            });

            if (authError) throw authError;
            if (!authData.user) throw new Error("Failed to create user.");

            // Create Profile with PASTOR role
            const { error: profileError } = await supabase.from('profiles').upsert({
                id: authData.user.id,
                email: newPastor.email,
                name: newPastor.name,
                role: UserRole.PASTOR,
                church_id: myChurch.id,
                voluntary_call: 'Pastor'
            });

            if (profileError) throw profileError;

            setMessage({ type: 'success', text: `Pastor ${newPastor.name} created! They can now log in.` });
            setNewPastor({ email: '', name: '', password: '' });

        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 p-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <Settings className="text-blue-600" /> Church Dashboard
                    </h1>
                    <p className="text-gray-500 mt-1">Managing {myChurch.name}</p>
                </div>
                <Link to={`/church/${myChurch.id}`} className="text-blue-600 font-medium hover:underline">
                    View Public Page
                </Link>
            </div>

            {message && (
                <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message.type === 'success' ? <Check className="w-5 h-5"/> : <AlertTriangle className="w-5 h-5"/>}
                    {message.text}
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex border-b border-gray-200">
                    <button 
                        onClick={() => setActiveTab('info')}
                        className={`flex-1 py-4 text-sm font-medium text-center transition ${activeTab === 'info' ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        General Info
                    </button>
                    <button 
                        onClick={() => setActiveTab('team')}
                        className={`flex-1 py-4 text-sm font-medium text-center transition ${activeTab === 'team' ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Pastoral Team
                    </button>
                </div>

                <div className="p-6">
                    {activeTab === 'info' && (
                        <form onSubmit={handleUpdateInfo} className="space-y-6 max-w-2xl mx-auto">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Church Name</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                    value={churchData.name}
                                    onChange={e => setChurchData({...churchData, name: e.target.value})}
                                />
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-gray-700">Address</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                    value={churchData.address}
                                    onChange={e => setChurchData({...churchData, address: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Website</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                    value={churchData.website}
                                    onChange={e => setChurchData({...churchData, website: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors h-32 resize-none"
                                    value={churchData.description}
                                    onChange={e => setChurchData({...churchData, description: e.target.value})}
                                />
                            </div>
                            <button 
                                type="submit" 
                                disabled={isLoading}
                                className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading ? 'Saving...' : 'Update Church Info'}
                            </button>
                        </form>
                    )}

                    {activeTab === 'team' && (
                        <div className="space-y-8">
                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <UserPlus className="text-blue-600"/> Create Pastor Account
                                </h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Create a login for a pastor. They will be automatically linked to this church and granted PASTOR permissions.
                                </p>
                                <form onSubmit={handleCreatePastor} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <input 
                                            type="text" 
                                            placeholder="Pastor Name"
                                            required
                                            className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-600"
                                            value={newPastor.name}
                                            onChange={e => setNewPastor({...newPastor, name: e.target.value})}
                                        />
                                    </div>
                                    <div>
                                        <input 
                                            type="email" 
                                            placeholder="Email Address"
                                            required
                                            className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-600"
                                            value={newPastor.email}
                                            onChange={e => setNewPastor({...newPastor, email: e.target.value})}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <input 
                                            type="password" 
                                            placeholder="Temporary Password"
                                            required
                                            className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-600"
                                            value={newPastor.password}
                                            onChange={e => setNewPastor({...newPastor, password: e.target.value})}
                                        />
                                    </div>
                                    <button 
                                        type="submit" 
                                        disabled={isLoading}
                                        className="md:col-span-2 bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {isLoading ? 'Creating Account...' : 'Create Pastor'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChurchDashboard;
